"use client"
import { useSettingsStore } from "@/lib/settings-store"
import { useSourceStore } from "@/lib/source-store"
import { useLoggerStore } from "@/lib/logger-store"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import { Button } from "@/components/ui/button"
import { useState, useEffect, useMemo } from "react"
import { ChevronDown, MessageSquare } from "@/lib/icons"
import { performancePresets } from "@/lib/performance-presets"
import { SettingsBackup } from "@/components/settings-backup"
import { Trash2 } from "@/lib/icons"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { TagFilterManager } from "@/components/tag-filter-manager"
import { BlacklistManager } from "@/components/blacklist-manager"
import { Input } from "@/components/ui/input"
import { TagCleanupTool } from "@/components/tag-cleanup-tool"
import { TagAliasManager } from "@/components/tag-alias-manager"
import { Sheet } from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { SHOW_PRIVACY_MODE, SHOW_SMART_SHUFFLE } from "@/lib/privacy-mode-visibility"
import { Download } from "@/lib/icons"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { RotateCcw } from "@/lib/icons"
import { FeedbackModal } from "@/components/feedback-modal"
import { useTranslations } from "next-intl"
import Link from "next/link"
import { useIsAdmin } from "@/lib/auth-store"

export function SettingsPanel() {
  const t = useTranslations("settingsPanel")
  const isAdmin = useIsAdmin()
  const {
    loadingMode,
    infiniteScroll,
    infinitePagesMode,
    pullToLoadEnabled,
    pullDistanceThreshold,
    postsPerPage,
    imageQuality,
    autoplayVideos,
    gridVideoPreviews,
    forcePreview,
    setForcePreview,
    albumHoverPreview,
    setAlbumHoverPreview,
    tagStepGesture,
    setTagStepGesture,
    tagStepEnd,
    setTagStepEnd,
    phraseToTags,
    handMode,
    imageNavDirection,
    infoSwipeDirection,
    gridLayout,
    gridFitShape,
    gridFitMode,
    gridFitBlur,
    gridFlowDirection,
    gridGap,
    gridColumns,
    gridColumnsAuto,
    borderRadius,
    nestedGridMode,
    nestedGridThreshold,
    perSourceNestedMode,
    showButtonLabels,
    hideScrollbar,
    shuffleMode,
    smartShuffleMode,
    stealthMode,
    saveSearchHistory,
    saveViewHistory,
    saveDownloadHistory,
    clearClipboardAfter,
    cacheImages,
    clearTempOnExit,
    passwordProtectedDownloads,
    hideFromRecentFiles,
    rotateUserAgents,
    stripReferrerHeaders,
    randomizeRequestTiming,
    panicButtonEnabled,
    panicAction,
    autoLockAfter,
    blurThumbnails,
    disableScreenshots,
    appLockType,
    panicButtonKeybind,
    theme,
    goonGlassesIntensity,
    booruModalLayout,
    eromeModalPositioning,
    setLoadingMode,
    setInfiniteScroll,
    setInfinitePagesMode,
    setPullToLoadEnabled,
    setPullDistanceThreshold,
    setPostsPerPage,
    setImageQuality,
    setAutoplayVideos,
    setGridVideoPreviews,
    setPhraseToTags,
    setHandMode,
    setImageNavDirection,
    setInfoSwipeDirection,
    setGridLayout,
    setGridFitShape,
    setGridFitMode,
    setGridFitBlur,
    setGridFlowDirection,
    setGridGap,
    setGridColumns,
    setGridColumnsAuto,
    setBorderRadius,
    setNestedGridMode,
    setNestedGridThreshold,
    setPerSourceNestedMode,
    setShowButtonLabels,
    setHideScrollbar,
    setShuffleMode,
    setSmartShuffleMode,
    setStealthMode,
    setSaveSearchHistory,
    setSaveViewHistory,
    setSaveDownloadHistory,
    setClearClipboardAfter,
    setCacheImages,
    setClearTempOnExit,
    setPasswordProtectedDownloads,
    setHideFromRecentFiles,
    setRotateUserAgents,
    setStripReferrerHeaders,
    setRandomizeRequestTiming,
    setPanicButtonEnabled,
    setPanicAction,
    setAutoLockAfter,
    setBlurThumbnails,
    setDisableScreenshots,
    setAppLockType,
    setPanicButtonKeybind,
    setTheme,
    setGoonGlassesIntensity,
    setBooruModalLayout,
    setEromeModalPositioning,
    mouseWheelBehavior,
    setMouseWheelBehavior,
    enableMousePan,
    setEnableMousePan,
    panSensitivity,
    setPanSensitivity,
    showScrollButtons,
    setShowScrollButtons,
    searchBarPosition,
    setSearchBarPosition,
    performanceMode,
    setPerformanceMode,
    cacheLimit,
    setCacheLimit,
    prefetchAmount,
    setPrefetchAmount,
    tagsScrollableMode,
    setTagsScrollableMode,
    saveLocation,
    setSaveLocation,
    customSavePath,
    setCustomSavePath,
    createSubfolder,
    setCreateSubfolder,
    autoDownloadWithoutDialog,
    setAutoDownloadWithoutDialog,
    videoPitchCorrection,
    setVideoPitchCorrection,
    videoAutoMute,
    setVideoAutoMute,
    videoDefaultSpeed,
    setVideoDefaultSpeed,
    handyPreviewPlaybackMode,
    setHandyPreviewPlaybackMode,
  } = useSettingsStore()

  const [expandedSections, setExpandedSections] = useState({
    browsing: false,
    grid: false,
    nestedGrid: false,
    interface: false,
    viewer: false,
    videoPlayer: false,
    stealth: false,
    performance: false,
    backup: false,
    storage: false,
    tags: false,
    display: false,
    content: false,
    appLock: false,
    downloads: false,
  })

  // For the per-source nested-grid override list — only sources the user
  // actually has enabled, so this doesn't turn into a 40-row wall.
  // Filtering happens in a separate useMemo (not inline in the selector)
  // because a selector that returns a fresh .filter() array every call
  // breaks Zustand's snapshot-equality caching and infinite-loops React.
  const allSources = useSourceStore((state) => state.sources)
  const enabledSources = useMemo(() => allSources.filter((s) => s.enabled && !s.disabled), [allSources])

  const [isCapturingKeybind, setIsCapturingKeybind] = useState(false)
  const [debugOpen, setDebugOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)

  const logs = useLoggerStore((state) => state.logs)
  const clearLogs = useLoggerStore((state) => state.clearLogs)
  const exportLogs = useLoggerStore((state) => state.exportLogs)
  const [debugFilter, setDebugFilter] = useState<"all" | "info" | "warn" | "error" | "debug">("all")

  const filteredLogs = debugFilter === "all" ? logs : logs.filter((log) => log.level === debugFilter)

  useEffect(() => {
    // Only while recording the panic-button shortcut. Without this guard the listener swallowed every key the whole time Settings was
    // open, so no text box in Settings could be typed into.
    if (!isCapturingKeybind) return
    const handleKeyDown = (e: KeyboardEvent) => {
      e.preventDefault()
      const keys: string[] = []
      if (e.ctrlKey) keys.push("Ctrl")
      if (e.shiftKey) keys.push("Shift")
      if (e.altKey) keys.push("Alt")
      if (e.metaKey) keys.push("Meta")
      if (e.key !== "Control" && e.key !== "Shift" && e.key !== "Alt" && e.key !== "Meta") {
        keys.push(e.key.toUpperCase())
      }
      if (keys.length > 1) {
        setPanicButtonKeybind(keys.join("+"))
        setIsCapturingKeybind(false)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isCapturingKeybind, setPanicButtonKeybind])

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  const applyPerformancePreset = (preset: "low" | "medium" | "high") => {
    const config = performancePresets[preset]
    setPerformanceMode(preset)
    setCacheLimit(config.cacheLimit)
    setPrefetchAmount(config.prefetchAmount)
    setGridColumns(config.gridColumns)
    setImageQuality(config.imageQuality)
    setInfiniteScroll(config.infiniteScroll)
    setAutoplayVideos(config.autoplayVideos)
  }

  const [isOpen, setIsOpen] = useState(true)

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <div className="glass glass-denser rounded-xl p-3 space-y-3">
        <Collapsible
          defaultOpen={expandedSections.browsing}
          onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, browsing: open }))}
        >
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("browsing")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandedSections.browsing && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 px-2 pt-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="loading-mode" className="min-w-0">{t("loadingMode")}</Label>
                <Select value={loadingMode} onValueChange={(v: any) => setLoadingMode(v)}>
                  <SelectTrigger id="loading-mode" className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t("autoRecommended")}</SelectItem>
                    <SelectItem value="infinite-scroll">{t("infiniteScroll")}</SelectItem>
                    <SelectItem value="pagination">{t("pagination")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="pull-to-load" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("pullToLoad")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("swipeUpToLoadMoreContent")}</span>
                </Label>
                <Switch id="pull-to-load" checked={pullToLoadEnabled} onCheckedChange={setPullToLoadEnabled} />
              </div>

              {pullToLoadEnabled && (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <Label htmlFor="pull-distance" className="flex min-w-0 flex-col gap-1 items-start">
                      <span>{t("pullDistance")}</span>
                      <span className="text-xs text-muted-foreground font-normal">
                        {t("swipeDistanceRequiredToTriggerLoad")}
                      </span>
                    </Label>
                    <span className="text-sm text-muted-foreground">{t("pullDistanceThresholdPx", { pullDistanceThreshold })}</span>
                  </div>
                  <Slider
                    id="pull-distance"
                    min={20}
                    max={200}
                    step={10}
                    value={[pullDistanceThreshold]}
                    onValueChange={(v) => setPullDistanceThreshold(v[0])}
                    className="w-full my-2"
                  />
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="posts-per-page" className="min-w-0">{t("postsPerPage")}</Label>
                <Select value={postsPerPage.toString()} onValueChange={(v) => setPostsPerPage(Number(v))}>
                  <SelectTrigger id="posts-per-page" className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="40">40</SelectItem>
                    <SelectItem value="60">60</SelectItem>
                    <SelectItem value="80">80</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="image-quality" className="min-w-0">{t("imageQuality")}</Label>
                <Select value={imageQuality} onValueChange={(v: any) => setImageQuality(v)}>
                  <SelectTrigger id="image-quality" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preview">{t("preview")}</SelectItem>
                    <SelectItem value="sample">{t("sample")}</SelectItem>
                    <SelectItem value="full">{t("full")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between pb-2">
                <Label htmlFor="autoplay-videos" className="min-w-0">{t("autoplayVideos")}</Label>
                <Switch id="autoplay-videos" checked={autoplayVideos} onCheckedChange={setAutoplayVideos} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
                <Label htmlFor="grid-video-previews" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("gridVideoPreviews")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("gridVideoPreviewsHint")}</span>
                </Label>
                <Select value={gridVideoPreviews} onValueChange={(v: any) => setGridVideoPreviews(v)}>
                  <SelectTrigger id="grid-video-previews" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hover">{t("gridVideoHover")}</SelectItem>
                    <SelectItem value="always">{t("gridVideoAlways")}</SelectItem>
                    <SelectItem value="off">{t("gridVideoOff")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
                <Label htmlFor="force-preview" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("forcePreview")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("forcePreviewHint")}</span>
                </Label>
                <Switch id="force-preview" checked={forcePreview} onCheckedChange={setForcePreview} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
                <Label htmlFor="album-hover-preview" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("albumHoverPreview")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("albumHoverPreviewHint")}</span>
                </Label>
                <Switch id="album-hover-preview" checked={albumHoverPreview} onCheckedChange={setAlbumHoverPreview} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
                <Label htmlFor="tag-step-gesture" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("tagStepGesture")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("tagStepGestureHint")}</span>
                </Label>
                <Switch id="tag-step-gesture" checked={tagStepGesture} onCheckedChange={setTagStepGesture} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
                <Label htmlFor="tag-step-end" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("tagStepEnd")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("tagStepEndHint")}</span>
                </Label>
                <Select value={tagStepEnd === "set" ? "set" : "continuous"} onValueChange={(v: any) => setTagStepEnd(v)} disabled={!tagStepGesture}>
                  <SelectTrigger id="tag-step-end" className="w-52">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="continuous">{t("tagStepEndContinuous")}</SelectItem>
                    <SelectItem value="set">{t("tagStepEndSet")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
                <Label htmlFor="phrase-to-tags" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("phraseToTags")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("phraseToTagsHint")}</span>
                </Label>
                <Switch id="phrase-to-tags" checked={phraseToTags} onCheckedChange={setPhraseToTags} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="shuffle-mode" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("shuffleMode")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("randomContent")}</span>
                </Label>
                <Switch id="shuffle-mode" checked={shuffleMode} onCheckedChange={setShuffleMode} />
              </div>

              {/* Hidden while payment processors review Goonru (owner order 2026-10-26): unbuilt. See lib/privacy-mode-visibility.ts. */}
              {SHOW_SMART_SHUFFLE && <div className="flex items-center justify-between opacity-60">
                <Label htmlFor="smart-shuffle" className="flex min-w-0 flex-col gap-1 items-start">
                  <span className="flex items-center gap-2">
                    {t("smartShuffle")}
                    <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">{t("comingSoon")}</Badge>
                  </span>
                  <span className="text-xs text-muted-foreground font-normal">{t("basedOnLikedTagsNotImplementedYet")}</span>
                </Label>
                <Switch id="smart-shuffle" checked={smartShuffleMode} onCheckedChange={setSmartShuffleMode} disabled />
              </div>}
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Collapsible
          defaultOpen={expandedSections.grid}
          onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, grid: open }))}
        >
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("gridLayout")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandedSections.grid && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 px-2 pt-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="grid-layout" className="flex min-w-0 flex-1 flex-col gap-1 items-start">
                  <span>{t("layoutStyle")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("squareOrMasonry")}</span>
                </Label>
                <Select value={gridLayout} onValueChange={(v: any) => setGridLayout(v)}>
                  <SelectTrigger id="grid-layout" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="square">{t("square")}</SelectItem>
                    <SelectItem value="masonry">{t("masonry")}</SelectItem>
                    <SelectItem value="fitted">{t("fitted")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="grid-flow" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("gridFlow")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("fillDirection")}</span>
                </Label>
                {/* Masonry places each card in the shortest column itself, so the flow choice only applies to the square grid. */}
                <Select value={gridLayout === "masonry" ? "auto" : gridFlowDirection} disabled={gridLayout === "masonry"} onValueChange={(v: any) => setGridFlowDirection(v)}>
                  <SelectTrigger id="grid-flow" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t("auto")}</SelectItem>
                    <SelectItem value="vertical">{t("horizontal")}</SelectItem>
                    <SelectItem value="horizontal">{t("vertical")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Fitted layout options: every cell is the same shape, the image is fitted into it. Greyed "Auto" elsewhere. */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="grid-fit-shape" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("cellShape")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("fittedOnly")}</span>
                </Label>
                <Select value={gridLayout === "fitted" ? gridFitShape : "auto"} disabled={gridLayout !== "fitted"} onValueChange={(v: any) => setGridFitShape(v)}>
                  <SelectTrigger id="grid-fit-shape" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t("auto")}</SelectItem>
                    <SelectItem value="landscape">{t("landscape")}</SelectItem>
                    <SelectItem value="portrait">{t("portrait")}</SelectItem>
                    <SelectItem value="square">{t("square")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="grid-fit-mode" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("imageFit")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("fittedOnly")}</span>
                </Label>
                <Select value={gridLayout === "fitted" ? gridFitMode : "auto"} disabled={gridLayout !== "fitted"} onValueChange={(v: any) => setGridFitMode(v)}>
                  <SelectTrigger id="grid-fit-mode" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t("auto")}</SelectItem>
                    <SelectItem value="contain">{t("fitFit")}</SelectItem>
                    <SelectItem value="cover">{t("fitCover")}</SelectItem>
                    <SelectItem value="fill">{t("fitFill")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="grid-fit-blur" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("blurredBackdrop")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("blurredBackdropHint")}</span>
                </Label>
                <Switch id="grid-fit-blur" checked={gridFitBlur} disabled={gridLayout !== "fitted" || gridFitMode !== "contain"} onCheckedChange={setGridFitBlur} />
              </div>

              <div className="flex flex-col gap-2 py-0">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="grid-columns-auto" className="flex min-w-0 flex-col gap-1 items-start">
                    <span>{t("gridColumns")}</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {gridColumnsAuto ? t("fitsAsManyColumnsAsYourScreenAllows") : t("fixedColumnCount")}
                    </span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{t("auto")}</span>
                    <Switch id="grid-columns-auto" checked={gridColumnsAuto} onCheckedChange={setGridColumnsAuto} />
                  </div>
                </div>
                {!gridColumnsAuto && (
                  <>
                    <div className="flex items-center justify-end">
                      <span className="text-sm text-muted-foreground">{gridColumns}</span>
                    </div>
                    <Slider
                      id="grid-columns"
                      min={1}
                      max={8}
                      step={1}
                      value={[gridColumns]}
                      onValueChange={(v) => setGridColumns(v[0])}
                      className="w-full my-2"
                    />
                  </>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="grid-gap" className="min-w-0">{t("gridGap")}</Label>
                  <span className="text-sm text-muted-foreground">{t("gridGapPx", { gridGap })}</span>
                </div>
                <Slider
                  id="grid-gap"
                  min={0}
                  max={16}
                  step={1}
                  value={[gridGap]}
                  onValueChange={(v) => setGridGap(v[0])}
                  className="w-full py-2"
                />
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="border-radius" className="flex min-w-0 flex-col gap-1 items-start">
                    <span>{t("roundedCorners")}</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {borderRadius === 0
                        ? "None"
                        : borderRadius <= 4
                          ? "Small"
                          : borderRadius <= 8
                            ? "Medium"
                            : "Large"}
                    </span>
                  </Label>
                  <span className="text-sm text-muted-foreground">{t("borderRadiusPx", { borderRadius })}</span>
                </div>
                <Slider
                  id="border-radius"
                  min={0}
                  max={16}
                  step={1}
                  value={[borderRadius]}
                  onValueChange={(v) => setBorderRadius(v[0])}
                  className="w-full py-2"
                />
              </div>

              <div className="pt-3 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    localStorage.removeItem("responsive-grid-set")
                    setGridLayout("masonry")
                    setGridColumns(5)
                    setGridGap(4)
                    setBorderRadius(6)
                  }}
                  className="gap-2 bg-transparent w-full"
                >
                  <RotateCcw className="h-4 w-4" />
                  {t("resetGridToDefaults")}
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Collapsible
          defaultOpen={expandedSections.nestedGrid}
          onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, nestedGrid: open }))}
        >
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("nestedGrid")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandedSections.nestedGrid && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 px-2 pt-3">
              <p className="text-xs text-muted-foreground -mt-1">
                {t("controlsWhetherOpeningAnAlbumBrowsesItsPa")}
              </p>

              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="nested-threshold" className="flex min-w-0 flex-col gap-1 items-start">
                    <span>{t("albumSizeThreshold")}</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {nestedGridThreshold === 0
                        ? "Disabled — size never decides the mode"
                        : `Albums with ${nestedGridThreshold}+ images open nested`}
                    </span>
                  </Label>
                  <span className="text-sm text-muted-foreground">{nestedGridThreshold === 0 ? "Off" : nestedGridThreshold}</span>
                </div>
                <Slider
                  id="nested-threshold"
                  min={0}
                  max={100}
                  step={5}
                  value={[nestedGridThreshold]}
                  onValueChange={(v) => setNestedGridThreshold(v[0])}
                  className="w-full py-2"
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="nested-global" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("defaultMode")}</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {t("fallbackWhenThresholdAndPerSourceBelowDon")}
                  </span>
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{nestedGridMode ? "Nested" : "Modal"}</span>
                  <Switch id="nested-global" checked={nestedGridMode} onCheckedChange={setNestedGridMode} />
                </div>
              </div>

              {enabledSources.length > 0 && (
                <div className="pt-3 border-t space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase">{t("perSourceOverride")}</p>
                  {enabledSources.map((source) => {
                    const override = perSourceNestedMode[source.id]
                    const value = override === undefined ? "auto" : override ? "nested" : "modal"
                    return (
                      <div key={source.id} className="flex flex-wrap items-center justify-between gap-3">
                        <Label htmlFor={`nested-source-${source.id}`} className="text-sm">
                          {source.name}
                        </Label>
                        <Select
                          value={value}
                          onValueChange={(v: "auto" | "nested" | "modal") =>
                            setPerSourceNestedMode(source.id, v === "auto" ? undefined : v === "nested")
                          }
                        >
                          <SelectTrigger id={`nested-source-${source.id}`} className="w-28 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">{t("auto")}</SelectItem>
                            <SelectItem value="nested">{t("nested")}</SelectItem>
                            <SelectItem value="modal">{t("modal")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Collapsible
          defaultOpen={expandedSections.interface}
          onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, interface: open }))}
        >
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("interface")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandedSections.interface && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 px-2 pt-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="theme" className="flex flex-col gap-0.5 items-start">
                  <span className="text-sm">{t("theme")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("chooseColorScheme")}</span>
                </Label>
                <Select value={theme} onValueChange={(v: any) => setTheme(v)}>
                  <SelectTrigger id="theme" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">{t("light")}</SelectItem>
                    <SelectItem value="auto">{t("autoTwilight")}</SelectItem>
                    <SelectItem value="dark">{t("dark")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="booru-modal-layout" className="flex flex-col gap-0.5 items-start">
                  <span className="text-sm">{t("booruModal")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("classicVsFullscreenViewer")}</span>
                </Label>
                <Select value={booruModalLayout} onValueChange={(v: any) => setBooruModalLayout(v)}>
                  <SelectTrigger id="booru-modal-layout" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="classic">{t("classic")}</SelectItem>
                    <SelectItem value="fullscreen">{t("fullscreen")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="erome-modal-positioning" className="flex flex-col gap-0.5 items-start">
                  <span className="text-sm">{t("eromePositioning")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("howEromePostsPositionTheModal")}</span>
                </Label>
                <Select value={eromeModalPositioning} onValueChange={(v: any) => setEromeModalPositioning(v)}>
                  <SelectTrigger id="erome-modal-positioning" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t("auto")}</SelectItem>
                    <SelectItem value="centered">{t("centered")}</SelectItem>
                    <SelectItem value="pinned">{t("pinned")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2 py-2 border-t pt-3 pb-1">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="goon-glasses" className="flex min-w-0 flex-col gap-1 items-start">
                    <span>{t("goonGlasses")}</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {t("yellowTintForBlueLightProtection")}
                    </span>
                  </Label>
                  <span className="text-sm text-muted-foreground">{goonGlassesIntensity}%</span>
                </div>
                <Slider
                  id="goon-glasses"
                  min={0}
                  max={100}
                  step={5}
                  value={[goonGlassesIntensity]}
                  onValueChange={(v) => setGoonGlassesIntensity(v[0])}
                  className="w-full py-1"
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="tags-scrollable" className="min-w-0">{t("tagsMode")}</Label>
                <Select
                  value={tagsScrollableMode ? "scroll" : "wrap"}
                  onValueChange={(v: any) => setTagsScrollableMode(v === "scroll")}
                >
                  <SelectTrigger id="tags-scrollable" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wrap">{t("wrap")}</SelectItem>
                    <SelectItem value="scroll">{t("scrollable")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="button-labels" className="min-w-0">{t("buttonLabels")}</Label>
                <Switch id="button-labels" checked={showButtonLabels} onCheckedChange={setShowButtonLabels} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="hide-scrollbar" className="min-w-0">{t("hideScrollbar")}</Label>
                <Switch id="hide-scrollbar" checked={hideScrollbar} onCheckedChange={setHideScrollbar} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="hand-mode" className="min-w-0">{t("handMode")}</Label>
                <Select value={handMode} onValueChange={(v: any) => setHandMode(v)}>
                  <SelectTrigger id="hand-mode" className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="right">{t("right")}</SelectItem>
                    <SelectItem value="left">{t("left")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="scroll-buttons" className="min-w-0">{t("showScrollButtons")}</Label>
                <Switch id="scroll-buttons" checked={showScrollButtons} onCheckedChange={setShowScrollButtons} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="search-position" className="min-w-0">{t("searchBarPosition")}</Label>
                <Select value={searchBarPosition} onValueChange={(v: any) => setSearchBarPosition(v)}>
                  <SelectTrigger id="search-position" className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">{t("top")}</SelectItem>
                    <SelectItem value="bottom">{t("bottom")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Collapsible
          defaultOpen={expandedSections.viewer}
          onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, viewer: open }))}
        >
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("viewerControls")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandedSections.viewer && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 px-1.5 pt-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="nav-direction" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("imageNavigation")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("scrollSwipeDirection")}</span>
                </Label>
                <Select value={imageNavDirection} onValueChange={(v: any) => setImageNavDirection(v)}>
                  <SelectTrigger id="nav-direction" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="horizontal-next-right">{t("right2")}</SelectItem>
                    <SelectItem value="horizontal-next-left">{t("left2")}</SelectItem>
                    <SelectItem value="vertical-next-down">{t("down")}</SelectItem>
                    <SelectItem value="vertical-next-up">{t("up")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="mouse-wheel" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("mouseWheel")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("scrollWheelBehavior")}</span>
                </Label>
                <Select value={mouseWheelBehavior} onValueChange={(v: any) => setMouseWheelBehavior(v)}>
                  <SelectTrigger id="mouse-wheel" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zoom">{t("zoomDefault")}</SelectItem>
                    <SelectItem value="navigate">{t("navigate")}</SelectItem>
                    <SelectItem value="pan">{t("panZoomed")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="enable-pan" className="flex min-w-0 flex-col gap-1 items-start">
                  <span className="text-center">{t("mousePan")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("dragToPanZoomedImages")}</span>
                </Label>
                <input
                  id="enable-pan"
                  type="checkbox"
                  checked={enableMousePan}
                  onChange={(e) => setEnableMousePan(e.target.checked)}
                  className="h-4 w-4 rounded border-input pr-0 mr-3"
                  aria-label={t("enableMousePanning")}
                  title={t("enableMousePanning")}
                />
              </div>

              {enableMousePan && (
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="pan-sensitivity" className="flex flex-col gap-1">
                    <span>{t("panSensitivity")}</span>
                    <span className="text-xs text-muted-foreground font-normal">{panSensitivity.toFixed(1)}x</span>
                  </Label>
                  <input
                    id="pan-sensitivity"
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.1"
                    value={panSensitivity}
                    onChange={(e) => setPanSensitivity(Number.parseFloat(e.target.value))}
                    className="w-24 h-2 bg-secondary/20 rounded-lg appearance-none cursor-pointer"
                    aria-label={t("panSensitivity2")}
                    title={t("panSensitivity2")}
                  />
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="info-direction" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("infoPanel")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("swipeToShowInfo")}</span>
                </Label>
                <Select value={infoSwipeDirection} onValueChange={(v: any) => setInfoSwipeDirection(v)}>
                  <SelectTrigger id="info-direction" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">{t("left2")}</SelectItem>
                    <SelectItem value="right">{t("right2")}</SelectItem>
                    <SelectItem value="up">{t("up")}</SelectItem>
                    <SelectItem value="down">{t("down")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Added Video Player section */}
        <Collapsible
          defaultOpen={expandedSections.videoPlayer}
          onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, videoPlayer: open }))}
        >
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("videoPlayer")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandedSections.videoPlayer && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 px-1.5 pt-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="video-pitch-correction" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("pitchCorrection")}</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {t("adjustAudioPitchWhenChangingSpeed")}
                  </span>
                </Label>
                <Switch
                  id="video-pitch-correction"
                  checked={videoPitchCorrection}
                  onCheckedChange={setVideoPitchCorrection}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="video-auto-mute" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("autoplayMuted")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("muteVideosByDefaultOnAutoplay")}</span>
                </Label>
                <Switch id="video-auto-mute" checked={videoAutoMute} onCheckedChange={setVideoAutoMute} />
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="video-default-speed" className="min-w-0">{t("defaultPlaybackSpeed")}</Label>
                  <span className="text-sm text-muted-foreground">{t("videoDefaultSpeedX", { videoDefaultSpeed })}</span>
                </div>
                <Slider
                  id="video-default-speed"
                  min={0.25}
                  max={4}
                  step={0.25}
                  value={[videoDefaultSpeed]}
                  onValueChange={(v) => setVideoDefaultSpeed(v[0])}
                  className="w-full my-2"
                />
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-border/50 pt-3">
                <Label htmlFor="handy-preview-mode" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("handyPreviewMode")}</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {t("instantIsBestWhileEditingSmoothPreparesH")}
                  </span>
                </Label>
                <Select value={handyPreviewPlaybackMode} onValueChange={(v: any) => setHandyPreviewPlaybackMode(v)}>
                  <SelectTrigger id="handy-preview-mode" className="w-28 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instant">{t("instant")}</SelectItem>
                    <SelectItem value="smooth">{t("smooth")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Collapsible
          defaultOpen={expandedSections.performance}
          onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, performance: open }))}
        >
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("performance")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandedSections.performance && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 mx-2 pt-3">
              <div className="flex items-center justify-between py-0 gap-0 px-0">
                <Label htmlFor="performance-mode" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("performanceMode")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("quickPresets")}</span>
                </Label>
                <Select value={performanceMode} onValueChange={(v: any) => setPerformanceMode(v)}>
                  <SelectTrigger id="performance-mode" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t("low")}</SelectItem>
                    <SelectItem value="medium">{t("medium")}</SelectItem>
                    <SelectItem value="high">{t("high")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 px-0">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="cache-limit" className="min-w-0">{t("cacheLimitMB")}</Label>
                  <span className="text-sm font-medium">{cacheLimit}</span>
                </div>
                <input
                  id="cache-limit"
                  type="range"
                  min="10"
                  max="500"
                  value={cacheLimit}
                  onChange={(e) => setCacheLimit(Number.parseInt(e.target.value))}
                  className="w-full"
                  aria-label={t("cacheLimit")}
                  title={t("cacheLimit")}
                />
              </div>

              <div className="space-y-2 px-0">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="prefetch-amount" className="min-w-0">{t("prefetchAmount")}</Label>
                  <span className="text-sm font-medium">{prefetchAmount}</span>
                </div>
                <input
                  id="prefetch-amount"
                  type="range"
                  min="1"
                  max="20"
                  value={prefetchAmount}
                  onChange={(e) => setPrefetchAmount(Number.parseInt(e.target.value))}
                  className="w-full"
                  aria-label={t("prefetchAmount2")}
                  title={t("prefetchAmount2")}
                />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Hidden while payment processors review Goonru (owner order 2026-10-25): every control here is unbuilt. See lib/privacy-mode-visibility.ts. */}
        {SHOW_PRIVACY_MODE && (
        <Collapsible
          defaultOpen={expandedSections.stealth}
          onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, stealth: open }))}
        >
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground flex items-center gap-2">
              {t("privacyMode")}
              <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">{t("comingSoon")}</Badge>
            </h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandedSections.stealth && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            {/* Nothing below is wired to real behavior yet — every control is
                disabled rather than removed so this reads as "not built" and
                not as a fake privacy guarantee someone could rely on. */}
            <div className="space-y-3 px-1 pt-3 opacity-60">
              <p className="text-xs text-muted-foreground italic px-0.5 -mt-1 mb-1">
                {t("theseControlsArenTImplementedYetAndWonT")}
              </p>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="stealth-mode" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("enableStealthMode")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("privacyProtectionSuite")}</span>
                </Label>
                <Switch id="stealth-mode" checked={stealthMode} onCheckedChange={setStealthMode} disabled />
              </div>

              <div className="border-t border-border/50 pt-3 mt-3 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase">{t("historyTracking")}</p>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="save-search" className="min-w-0">{t("saveSearchHistory")}</Label>
                  <Switch id="save-search" checked={saveSearchHistory} onCheckedChange={setSaveSearchHistory} disabled />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="save-view" className="min-w-0">{t("saveViewHistory")}</Label>
                  <Switch id="save-view" checked={saveViewHistory} onCheckedChange={setSaveViewHistory} disabled />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="save-download" className="min-w-0">{t("saveDownloadHistory")}</Label>
                  <Switch id="save-download" checked={saveDownloadHistory} onCheckedChange={setSaveDownloadHistory} disabled />
                </div>
              </div>

              <div className="border-t border-border/50 pt-3 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase">{t("storageFiles")}</p>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="cache-images" className="min-w-0">{t("cacheImagesLocally")}</Label>
                  <Switch id="cache-images" checked={cacheImages} onCheckedChange={setCacheImages} disabled />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="clear-temp" className="min-w-0">{t("clearTempFilesOnExit")}</Label>
                  <Switch id="clear-temp" checked={clearTempOnExit} onCheckedChange={setClearTempOnExit} disabled />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="protected-downloads" className="min-w-0">{t("passwordProtectedDownloads")}</Label>
                  <Switch
                    id="protected-downloads"
                    checked={passwordProtectedDownloads}
                    onCheckedChange={setPasswordProtectedDownloads}
                    disabled
                  />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="hide-recent" className="min-w-0">{t("hideDownloadsFromRecentFiles")}</Label>
                  <Switch id="hide-recent" checked={hideFromRecentFiles} onCheckedChange={setHideFromRecentFiles} disabled />
                </div>
              </div>

              <div className="border-t border-border/50 pt-3 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase">{t("networkPrivacy")}</p>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="rotate-ua" className="min-w-0">{t("rotateUserAgents")}</Label>
                  <Switch id="rotate-ua" checked={rotateUserAgents} onCheckedChange={setRotateUserAgents} disabled />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="strip-referrer" className="min-w-0">{t("stripReferrerHeaders")}</Label>
                  <Switch
                    id="strip-referrer"
                    checked={stripReferrerHeaders}
                    onCheckedChange={setStripReferrerHeaders}
                    disabled
                  />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="randomize-timing" className="min-w-0">{t("randomizeRequestTiming")}</Label>
                  <Switch
                    id="randomize-timing"
                    checked={randomizeRequestTiming}
                    onCheckedChange={setRandomizeRequestTiming}
                    disabled
                  />
                </div>
              </div>

              <div className="border-t border-border/50 pt-3 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase">{t("uISecurity")}</p>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="blur-thumbnails" className="min-w-0">{t("blurThumbnailsUntilHover")}</Label>
                  <Switch id="blur-thumbnails" checked={blurThumbnails} onCheckedChange={setBlurThumbnails} disabled />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label htmlFor="disable-screenshots" className="min-w-0">{t("disableScreenshots")}</Label>
                  <Switch
                    id="disable-screenshots"
                    checked={disableScreenshots}
                    onCheckedChange={setDisableScreenshots}
                    disabled
                  />
                </div>
                <div className="hidden md:block space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="panic-button" className="text-xs">
                      {t("keybind")}
                    </Label>
                    <button
                      disabled
                      className="flex-1 text-left px-3 py-1.5 text-xs glass rounded-md cursor-not-allowed"
                    >
                      {panicButtonKeybind}
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <Label htmlFor="panic-action" className="text-sm">
                      {t("panicAction")}
                    </Label>
                    <Select value={panicAction} onValueChange={(v: any) => setPanicAction(v)} disabled>
                      <SelectTrigger id="panic-action" className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="close">{t("closeApp")}</SelectItem>
                        <SelectItem value="fake-screen">{t("fakeScreen")}</SelectItem>
                        <SelectItem value="clear-close">{t("clearClose")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="border-t border-border/50 pt-3 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase">{t("appLock")}</p>
              <p className="text-xs text-muted-foreground italic px-2 -mt-1 mb-1">
                {t("noLockScreenExistsYetTheseControlsDonT")}
              </p>
              <div className="flex items-center justify-between px-2">
                <Label htmlFor="lock-type" className="min-w-0">{t("lockType")}</Label>
                <Select value={appLockType} onValueChange={(v: any) => setAppLockType(v)} disabled>
                  <SelectTrigger id="lock-type" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("none")}</SelectItem>
                    <SelectItem value="pin">PIN</SelectItem>
                    <SelectItem value="password">{t("password")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {autoLockAfter !== null && (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <Label htmlFor="auto-lock" className="min-w-0">{t("autoLockAfterMinutes")}</Label>
                    <span className="text-sm text-muted-foreground">{autoLockAfter}</span>
                  </div>
                  <Slider
                    id="auto-lock"
                    min={1}
                    max={60}
                    step={1}
                    value={[autoLockAfter]}
                    onValueChange={(v) => setAutoLockAfter(v[0])}
                    className="w-full"
                    disabled
                  />
                </div>
              )}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
        )}

        <Collapsible
          defaultOpen={expandedSections.tags}
          onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, tags: open }))}
        >
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("tagsSection")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandedSections.tags && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            {/* Four tag tools in one place. Each keeps its own data and behavior: Love/Hate and Block both
                hide posts, but Block also covers users and combination rules. */}
            <Tabs defaultValue="lovehate" className="pt-3">
              <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-4">
                <TabsTrigger value="lovehate" className="text-xs">{t("tabLoveHate")}</TabsTrigger>
                <TabsTrigger value="block" className="text-xs">{t("tabBlock")}</TabsTrigger>
                <TabsTrigger value="aliases" className="text-xs">{t("tabAliases")}</TabsTrigger>
                <TabsTrigger value="cleanup" className="text-xs">{t("tabCleanup")}</TabsTrigger>
              </TabsList>
              <TabsContent value="lovehate" className="space-y-2 pt-3">
                <p className="text-xs text-muted-foreground">{t("loveHateHelp")}</p>
                <TagFilterManager />
              </TabsContent>
              <TabsContent value="block" className="space-y-2 pt-3">
                <p className="text-xs text-muted-foreground">{t("blockHelp")}</p>
                <BlacklistManager />
              </TabsContent>
              <TabsContent value="aliases" className="space-y-2 pt-3">
                <p className="text-xs text-muted-foreground">{t("aliasesHelp")}</p>
                <TagAliasManager />
              </TabsContent>
              <TabsContent value="cleanup" className="space-y-2 pt-3">
                <p className="text-xs text-muted-foreground">{t("cleanupHelp")}</p>
                <TagCleanupTool />
              </TabsContent>
            </Tabs>
          </CollapsibleContent>
        </Collapsible>

        <Collapsible
          defaultOpen={expandedSections.backup}
          onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, backup: open }))}
        >
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("backupRestore")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandedSections.backup && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="pt-3">
              <SettingsBackup />
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Collapsible
          defaultOpen={expandedSections.storage}
          onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, storage: open }))}
        >
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("storageDownload")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandedSections.storage && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 px-2 pt-3">
              <div className="flex items-center justify-between pr-1.5">
                <Label htmlFor="save-location" className="min-w-0">{t("saveLocation")}</Label>
                <Select value={saveLocation} onValueChange={(v: any) => setSaveLocation(v)}>
                  <SelectTrigger id="save-location" className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="downloads">{t("downloads")}</SelectItem>
                    <SelectItem value="documents">{t("documents")}</SelectItem>
                    <SelectItem value="custom">{t("customPath")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {saveLocation === "custom" && (
                <div className="space-y-3">
                  <Label htmlFor="custom-path" className="min-w-0">{t("customPath")}</Label>
                  <Input
                    id="custom-path"
                    placeholder={t("pathToSave")}
                    value={customSavePath}
                    onChange={(e) => setCustomSavePath(e.target.value)}
                  />
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="create-subfolder" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("createSubfolder")}</span>
                  <span className="text-xs text-muted-foreground font-normal">{t("organizeDownloadsByType")}</span>
                </Label>
                <Switch id="create-subfolder" checked={createSubfolder} onCheckedChange={setCreateSubfolder} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor="auto-download" className="flex min-w-0 flex-col gap-1 items-start">
                  <span>{t("downloadAutomatically")}</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {t("offAsksWhereToSaveEachDownloadAppliesTo")}
                  </span>
                </Label>
                <Switch id="auto-download" checked={autoDownloadWithoutDialog} onCheckedChange={setAutoDownloadWithoutDialog} />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {isAdmin && (
        <Collapsible defaultOpen={debugOpen} onOpenChange={setDebugOpen} className="border-b border-border/50">
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("debugLogs")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", debugOpen && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 px-2 pb-2 pt-3">
              <div className="flex gap-2 flex-wrap">
                {["all", "info", "warn", "error", "debug"].map((level) => (
                  <button
                    key={level}
                    onClick={() => setDebugFilter(level as "all" | "info" | "warn" | "error" | "debug")}
                    className={`text-xs px-2 py-1 rounded transition-colors ${
                      debugFilter === level
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary hover:bg-secondary/80 text-secondary-foreground hover:text-secondary-foreground"
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>

              <ScrollArea className="h-64 border border-border rounded-lg">
                <div className="p-2 space-y-1">
                  {filteredLogs.length === 0 ? (
                    <p className="text-xs text-muted-foreground p-2">{t("noLogs")}</p>
                  ) : (
                    filteredLogs.map((log, idx) => (
                      <div key={idx} className="text-xs font-mono bg-secondary/40 p-2 rounded">
                        <div className="flex gap-2 items-start flex-wrap">
                          <Badge className="text-xs h-5">{t("all")}</Badge>
                          <span className="text-muted-foreground text-[10px]">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="mt-1 text-foreground">[{log.source}]</div>
                        <div className="text-foreground/80 break-words">{log.message}</div>
                        {log.data != null && (
                          <div className="text-foreground/60 break-words text-[10px]">
                            {JSON.stringify(log.data, null, 2)}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>

              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={clearLogs} className="h-8 text-xs gap-1 bg-transparent">
                  <Trash2 className="h-3 w-3" />
                  {t("clearLogs")}
                </Button>
                <Button size="sm" variant="outline" onClick={exportLogs} className="h-8 text-xs gap-1 bg-transparent">
                  <Download className="h-3 w-3" />
                  {t("exportLogs")}
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
        )}

        {/* Feedback Section */}
        <Collapsible defaultOpen={feedbackOpen} onOpenChange={setFeedbackOpen} className="border-b border-border/50">
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
            <h3 className="text-xs font-semibold text-foreground">{t("feedback")}</h3>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", feedbackOpen && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3 px-2 pb-2 pt-3">
              <p className="text-xs text-muted-foreground">
                {t("reportBugsIssuesOrSuggestImprovementsYo")}
              </p>
              
              <Button 
                onClick={() => setFeedbackModalOpen(true)} 
                className="w-full"
                variant="default"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                {t("sendFeedback")}
              </Button>

              <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-border/30">
                <p className="font-medium">{t("whatGetsSent")}</p>
                <ul className="list-disc list-inside space-y-0.5 pl-2">
                  <li>{t("yourDescriptionOfTheIssue")}</li>
                  <li>{t("last20ActionsYouTook")}</li>
                  <li>{t("currentPageURL")}</li>
                  <li>{t("browserInformation")}</li>
                </ul>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Legal pages: otherwise only reachable from the footer of the legal pages themselves. */}
        <nav aria-label={t("footerLegal")} className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-2 py-3 text-xs text-muted-foreground">
          <Link href="/legal" className="underline-offset-2 hover:text-foreground hover:underline">{t("footerLegal")}</Link>
          <span aria-hidden="true">·</span>
          <Link href="/legal/privacy" className="underline-offset-2 hover:text-foreground hover:underline">{t("footerPrivacy")}</Link>
          <span aria-hidden="true">·</span>
          <Link href="/legal/terms" className="underline-offset-2 hover:text-foreground hover:underline">{t("footerTerms")}</Link>
          <span aria-hidden="true">·</span>
          <Link href="/legal/cookies" className="underline-offset-2 hover:text-foreground hover:underline">{t("footerCookies")}</Link>
          <span aria-hidden="true">·</span>
          <Link href="/legal/dmca" className="underline-offset-2 hover:text-foreground hover:underline">{t("footerDmca")}</Link>
        </nav>
      </div>
      
      {/* Feedback Modal */}
      <FeedbackModal open={feedbackModalOpen} onOpenChange={setFeedbackModalOpen} />
    </Sheet>
  )
}
